import { supabase } from '@/lib/supabase';
import type { PartnerEmbedEntitlementInput, PartnerEmbedEntitlementOutcome } from '../index';

function denied(): PartnerEmbedEntitlementOutcome {
  return { authority: 'denied' };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function decodeEntitlements(value: unknown): PartnerEmbedEntitlementOutcome {
  if (
    !isRecord(value) ||
    value.success !== true ||
    typeof value.allow_sign_in !== 'boolean' ||
    typeof value.whitelabel_runtime !== 'boolean'
  ) {
    return denied();
  }

  return {
    authority: 'approved',
    signInEnabled: value.allow_sign_in,
    whitelabelRuntimeEnabled: value.whitelabel_runtime,
  };
}

/**
 * Resolves the public partner embed permissions for one origin.
 * This operation always resolves to an outcome; RPC and decoding failures deny both permissions.
 */
export async function getPartnerEmbedEntitlements({
  partnerId,
  origin,
}: PartnerEmbedEntitlementInput): Promise<PartnerEmbedEntitlementOutcome> {
  if (!partnerId.trim() || !origin.trim()) return denied();

  try {
    const { data, error } = await supabase.rpc('get_public_partner_embed_entitlements', {
      p_partner_id: partnerId,
      p_domain: origin,
    });
    if (error) return denied();
    return decodeEntitlements(data);
  } catch {
    return denied();
  }
}
